import { Button, InputSelect } from '@vrplatform/components';
import { showApiErrorNotification } from '@vrplatform/data';
import { Box, Group, Modal, Text } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import {
  useOwnerStatementSummaryLayoutOptionsQuery,
  type OwnerStatementSummaryLayoutOption,
} from './useOwnerStatementSummaryLayoutOptionsQuery';

export const OwnerStatementSummariesBulkPdfExportModal = ({
  opened,
  onClose,
  onSubmit,
  loading,
  selectedCount,
  totalCount,
}: {
  opened: boolean;
  onClose: () => void;
  onSubmit: (layoutId: string) => Promise<void> | void;
  loading: boolean;
  selectedCount: number;
  totalCount: number;
}) => {
  const queryData = useOwnerStatementSummaryLayoutOptionsQuery();
  const [layout, setLayout] =
    useState<OwnerStatementSummaryLayoutOption | null>(null);

  const exportCount = selectedCount > 0 ? selectedCount : totalCount;

  useEffect(() => {
    if (!opened || layout || !queryData.data?.defaultLayoutId) return;

    const defaultOption =
      queryData.data.options.find(
        (option) => option.value === queryData.data?.defaultLayoutId
      ) ?? null;

    setLayout(defaultOption);
  }, [
    layout,
    opened,
    queryData.data?.defaultLayoutId,
    queryData.data?.options,
  ]);

  useEffect(() => {
    if (opened) return;
    setLayout(null);
  }, [opened]);

  const helperText = useMemo(() => {
    if (!totalCount) {
      return 'No owner statement summaries are currently available to export.';
    }

    if (!selectedCount) {
      return `No owners selected. Export will include all ${totalCount} owners currently shown in the list.`;
    }

    return `Export will include ${selectedCount} selected owner${selectedCount === 1 ? '' : 's'}.`;
  }, [selectedCount, totalCount]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      title="Export owner summary PDFs"
      size={520}
    >
      <Box
        component="form"
        onSubmit={async (event) => {
          event.preventDefault();

          if (!layout?.value) return;

          try {
            await onSubmit(layout.value);
            onClose();
          } catch (error) {
            showApiErrorNotification({
              error,
              title: "Couldn't download PDFs",
            });
          }
        }}
      >
        <Group align="stretch" gap="md" grow>
          <Box>
            <Text fw={500}>Owners</Text>
            <Text c="gray" size="sm" mt={4}>
              {helperText}
            </Text>
          </Box>

          <Box>
            <Text fw={500}>Layout</Text>
            <Text c="gray" size="sm" mt={4}>
              Choose the layout that should be used for every generated PDF.
            </Text>
            <Box mt="sm">
              <InputSelect
                value={layout}
                type="single"
                data={{
                  options: queryData.data?.options ?? [],
                  sort: null,
                  loading: queryData.isLoading || queryData.isFetching,
                }}
                setValue={(value) => {
                  setLayout(value as OwnerStatementSummaryLayoutOption | null);
                }}
                inputProps={{
                  placeholder: 'Select layout',
                  disabled:
                    queryData.isLoading ||
                    queryData.isFetching ||
                    !!queryData.error ||
                    !queryData.data?.options.length,
                }}
              />
            </Box>
            {queryData.error && (
              <Text c="red" size="sm" mt={8}>
                We failed to load statement layouts. Please try again.
              </Text>
            )}
          </Box>
        </Group>

        <Group justify="flex-end" mt="xl">
          <Button variant="transparent" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            loading={loading}
            type="submit"
            disabled={
              !layout?.value ||
              !exportCount ||
              queryData.isLoading ||
              queryData.isFetching ||
              !!queryData.error
            }
          >
            Download PDFs
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};
